/*
 * Planigo
 * Auteur  : Stéphane Raverdy
 * Société : SCIAD 2026
 */
import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  ArrowRight,
  Eye,
  EyeSlash,
  ShieldCheck,
} from '@phosphor-icons/react';
import { APP_VERSION } from '../lib/version';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;


export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [logoSettings, setLogoSettings] = useState({});
  const [loginBackgroundUrl, setLoginBackgroundUrl] = useState(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API_URL}/api/public/logo`).then(res => {
      if (res.data?.login_background_url) setLoginBackgroundUrl(res.data.login_background_url);
      if (res.data) setLogoSettings({
        login_height: res.data.logo_login_height || 86,
        login_width: res.data.logo_login_width || 320,
        form_height: res.data.logo_form_height || 74,
        form_width: res.data.logo_form_width || 300,
        logo_url: res.data.logo_url || null,
      });
    }).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(email, password);
    if (result.success) navigate('/');
    else setError(result.error);
    setLoading(false);
  };

  const rightLogo = logoSettings.logo_url ? `${API_URL}${logoSettings.logo_url}` : '/planigo-logo.png';
  const showcaseImage = loginBackgroundUrl ? `${API_URL}${loginBackgroundUrl}` : '/login-showcase-planigo.jpg';

  return (
    <div className="login-v2202">
      <section className="login-showcase login-showcase-image" aria-label="Présentation Planigo">
        <img
          src={showcaseImage}
          alt="Planigo - gérez vos projets et interventions avec efficacité"
          className="login-showcase-poster"
          onError={(e)=>{e.currentTarget.onerror=null;e.currentTarget.src='/login-showcase-planigo.jpg';}}
        />
      </section>

      <section className="login-panel">
        <div className="login-form-card">
          <div className="login-form-header">
            <img
              src={rightLogo}
              alt="Planigo"
              onError={(e)=>{e.currentTarget.onerror=null;e.currentTarget.src='/planigo-logo.png';}}
              className="login-form-logo"
              style={{ maxHeight: `${Math.min(Number(logoSettings.form_height || 74), 92)}px`, maxWidth: `${Math.min(Number(logoSettings.form_width || 300), 330)}px` }}
            />
            <div className="login-form-accent" />
            <h2>Connexion</h2>
            <p>Accédez à votre espace de gestion de projets</p>
          </div>

          {error && (
            <Alert variant="destructive" className="mb-5">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">Adresse e-mail</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="votre@entreprise.com"
                required
                className="login-input"
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <Label htmlFor="password">Mot de passe</Label>
                <Link to="/forgot-password" className="login-forgot" data-testid="forgot-password-link">
                  Mot de passe oublié ?
                </Link>
              </div>
              <div className="login-password-wrap">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Votre mot de passe"
                  required
                  className="login-input pr-12"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  className="login-password-toggle"
                  onClick={() => setShowPassword(v => !v)}
                  aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                >
                  {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <Button type="submit" className="login-submit" disabled={loading} data-testid="login-submit-btn">
              {loading ? 'Connexion...' : 'Se connecter'}
              <ArrowRight size={18} className="ml-2" />
            </Button>
          </form>

          <div className="login-security-note">
            <ShieldCheck size={24} weight="duotone" />
            <span>L'accès à cette application est réservé aux collaborateurs et partenaires autorisés.</span>
          </div>

          <div className="login-legal">
            <p>Planigo v{APP_VERSION} - Plateforme de gestion de projets IT</p>
            <p>Développé par la SCIAD</p>
            <p>Tous droits réservés 2026</p>
          </div>
        </div>
      </section>
    </div>
  );
}
